import { Loader2, ShieldAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from './AuthContext';

interface RequireAuthProps {
  children: ReactNode;
  /** Optional capability gate, e.g. "users:write". */
  permission?: string;
}

/**
 * Route guard. Waits for the session-restore attempt before redirecting, so a
 * page refresh does not bounce a signed-in user to the login screen.
 */
export function RequireAuth({ children, permission }: RequireAuthProps) {
  const { session, isBootstrapping, can } = useAuth();
  const location = useLocation();

  if (isBootstrapping) {
    return (
      <div className="grid min-h-[50vh] place-items-center">
        <Loader2 className="size-5 animate-spin text-muted-foreground" aria-label="Loading" />
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (permission && !can(permission)) {
    return (
      <div className="grid min-h-[50vh] place-items-center px-4">
        <div className="flex max-w-sm flex-col items-center gap-3 text-center">
          <span className="grid size-12 place-items-center rounded-full bg-danger-muted text-danger ring-1 ring-danger/20">
            <ShieldAlert className="size-6" aria-hidden />
          </span>
          <div>
            <p className="font-medium">You do not have access to this page</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Ask a workspace owner or admin if you think this is wrong.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
